import { CommonModule } from '@angular/common';
import { Component, computed, input, ChangeDetectionStrategy } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import { format } from 'date-fns';
import { AppCommonModule } from 'src/app/common.module';
import { Site } from 'src/app/models/site.model';
import { RoomTicket } from 'src/app/models/ticket.model';
import { getTaskAreaLabel } from 'src/app/services/worker-tickets.service';
import { convertTimestamp } from 'src/app/utility/time';
import { PrintLayoutComponent } from 'src/app/ui/print-layout/print-layout.component';
import { PrintLayoutSectionComponent } from 'src/app/ui/print-layout/print-layout-section.component';

export interface SiteReportPrintPayload {
  printId: string;
  site: Site;
  reportName: string;
  creatorName: string;
  createdAt: Date | Timestamp | string | number;
  clientName?: string | null;
  tickets: RoomTicket[];
  includeImages?: boolean;
  historyLimit?: number;
  labels: {
    site: string;
    users: string;
    tasks: string;
    created: string;
    inProgress: string;
    done: string;
    room: string;
    assignees: string;
    deadline: string;
  };
}

@Component({
  selector: 'app-site-report-print',
  standalone: true,
  imports: [
    AppCommonModule,
    CommonModule,
    PrintLayoutComponent,
    PrintLayoutSectionComponent,
  ],
  templateUrl: './site-report-print.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './site-report-print.component.scss',
})
export class SiteReportPrintComponent {
  payload = input.required<SiteReportPrintPayload>();

  readonly printableTickets = computed(() => (this.payload().tickets ?? []).map((ticket) => ({
    ...ticket,
    roomLabel: getTaskAreaLabel(ticket),
  })));

  readonly printSummaryItems = computed(() => {
    const payload = this.payload();
    const tickets = this.printableTickets();
    const doneCount = tickets.filter((ticket) => ['done', 'checked'].includes(ticket.status)).length;
    const inProgressCount = tickets.filter((ticket) => ticket.status === 'inProgress').length;
    const openCount = tickets.length - doneCount - inProgressCount;

    return [
      {
        label: payload.labels.site,
        value: payload.site?.name ?? '-',
        support: payload.clientName ?? payload.site?.clientName ?? payload.site?.client ?? '-',
        wide: true,
      },
      {
        label: payload.labels.users,
        value: payload.creatorName ?? '-',
      },
      {
        label: payload.labels.tasks,
        value: tickets.length,
      },
      {
        label: payload.labels.created,
        value: openCount,
      },
      {
        label: payload.labels.inProgress,
        value: inProgressCount,
      },
      {
        label: payload.labels.done,
        value: doneCount,
      },
    ];
  });

  readonly includeImages = computed(() => this.payload().includeImages ?? true);
  readonly historyLimit = computed(() => this.payload().historyLimit ?? 3);

  getStatusClass(ticket: RoomTicket) {
    if (['done', 'checked'].includes(ticket.status)) {
      return 'a4-pdf-card__status-pill--done';
    }
    if (ticket.status === 'inProgress') {
      return 'a4-pdf-card__status-pill--in-progress';
    }
    if (ticket.status === 'blocked' || ticket.status === 'late' || ticket.status === 'declined') {
      return 'a4-pdf-card__status-pill--blocked';
    }
    if (ticket.status === 'paused') {
      return 'a4-pdf-card__status-pill--paused';
    }
    return 'a4-pdf-card__status-pill--open';
  }

  getTicketAssigneeLabel(ticket: RoomTicket) {
    const usernames = ticket.usernames?.filter(Boolean) ?? [];
    if (usernames.length > 0) {
      return usernames.join(', ');
    }
    return ticket.username ?? null;
  }

  getDeadlineOnly(ticket: RoomTicket) {
    return this.toDate(ticket.deadline);
  }

  formatHistoryTimestamp(value: Date | Timestamp | string | number | null | undefined) {
    const date = this.toDate(value);
    if (!date) {
      return '';
    }
    return format(date, 'd.M.yyyy HH:mm');
  }

  private toDate(value: Date | Timestamp | string | number | null | undefined) {
    const date = convertTimestamp(value);
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
  }
}
